/** Bulk create-orders CSV: sample, parse, enrich, process */

import databaseAPI from '../../api/databaseAPI.js';
import { authFetch, authHeaders } from '../../api/auth';

export const downloadOrderCreateSampleCsv = () => {
  const sample = `customer_email,first_name,last_name,currency,variant_id,quantity,address1,city,state,country,zip,phone,coupon\n` +
    `user1@example.com,Asha,Verma,INR,REAL_VARIANT_ID_1,1,123 Street,Delhi,Delhi,India,110001,9999999999,\n` +
    `user2@example.com,John,Doe,USD,REAL_VARIANT_ID_2,2,45 Avenue,New York,NY,USA,10001,5551234567,SAVE10`;
  const blob = new Blob([sample], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'sample_create_orders.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const splitCsvLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = false; }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') { result.push(current.trim()); current = ''; }
      else { current += ch; }
    }
  }
  result.push(current.trim());
  return result;
};

/**
 * Pure parse of create-orders CSV text.
 * @returns {{ rows: Array, error: string|null }}
 */
export const parseOrderCreateCsvText = (csvText) => {
  try {
    let csv = csvText || '';
    if (!csv.trim()) {
      return { rows: [], error: 'CSV is empty' };
    }
    if (csv.charCodeAt(0) === 0xFEFF) csv = csv.substring(1);
    const lines = csv.split('\n').filter(l => l.trim().length > 0);

    const headers = splitCsvLine(lines[0]).map(h => h.trim().toLowerCase());
    const idx = (name) => headers.findIndex(h => h.replace(/[^a-z0-9]/g, '') === name.replace(/[^a-z0-9]/g, ''));
    const anyIdx = (names) => {
      for (const n of names) { const i = idx(n); if (i !== -1) return i; }
      return -1;
    };
    const emailIdx = anyIdx(['customer_email', 'email']);
    const firstNameIdx = anyIdx(['first_name', 'customer_first_name']);
    const lastNameIdx = anyIdx(['last_name', 'customer_last_name']);
    const currencyIdx = anyIdx(['currency']);
    const variantIdx = anyIdx(['variant_id']);
    const qtyIdx = anyIdx(['quantity', 'qty']);
    const addr1Idx = anyIdx(['address1', 'address_1', 'addr1']);
    const cityIdx = anyIdx(['city']);
    const stateIdx = anyIdx(['state', 'province']);
    const countryIdx = anyIdx(['country']);
    const zipIdx = anyIdx(['zip', 'pincode', 'postal_code', 'postcode']);
    const phoneIdx = anyIdx(['phone', 'phone_number', 'mobile']);
    const couponIdx = anyIdx(['coupon', 'coupon_code', 'discount_code']);

    if (emailIdx === -1 || variantIdx === -1 || qtyIdx === -1) {
      return { rows: [], error: 'CSV must include at least: customer_email, variant_id, quantity' };
    }

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = splitCsvLine(lines[i]);
      if (!values[emailIdx] || !values[variantIdx]) continue;
      rows.push({
        customer_email: values[emailIdx],
        customer_first_name: firstNameIdx !== -1 ? (values[firstNameIdx] || '') : '',
        customer_last_name: lastNameIdx !== -1 ? (values[lastNameIdx] || '') : '',
        currency: currencyIdx !== -1 ? (values[currencyIdx] || 'INR') : 'INR',
        variant_id: values[variantIdx],
        quantity: parseInt(values[qtyIdx] || '1', 10) || 1,
        product_title: '',
        price: '',
        coupon_code: couponIdx !== -1 ? (values[couponIdx] || '') : '',
        shipping_address: {
          address1: addr1Idx !== -1 ? (values[addr1Idx] || '') : '',
          city: cityIdx !== -1 ? (values[cityIdx] || '') : '',
          province: stateIdx !== -1 ? (values[stateIdx] || '') : '',
          country: countryIdx !== -1 ? (values[countryIdx] || 'India') : 'India',
          zip: zipIdx !== -1 ? (values[zipIdx] || '') : '',
          phone_number: phoneIdx !== -1 ? (values[phoneIdx] || '') : ''
        }
      });
    }

    if (rows.length === 0) {
      return { rows: [], error: 'No valid rows found. Ensure required columns are present.' };
    }
    return { rows, error: null };
  } catch (err) {
    console.error('Order CSV parse error', err);
    return { rows: [], error: 'Failed to parse CSV' };
  }
};

export const fetchVariantById = async (variantId, { baseURL, fetchFn = authFetch, headersFn = authHeaders } = {}) => {
  try {
    const apiBase = baseURL || databaseAPI.baseURL;
    const url = `${apiBase}/variants/${variantId}/`;
    const resp = await fetchFn(url, { method: 'GET', headers: headersFn({ 'Content-Type': 'application/json' }) });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data && data.success && data.variant) return data.variant;
    return null;
  } catch (_e) {
    return null;
  }
};

export const enrichCsvRowsWithVariantInfo = async (rows, fetchVariant = fetchVariantById) => {
  const ids = Array.from(new Set(rows.map(r => String(r.variant_id || '').trim()).filter(Boolean)));
  const idToVariant = new Map();
  for (const id of ids) {
    const v = await fetchVariant(id);
    idToVariant.set(id, v);
  }
  return rows.map(r => {
    const key = String(r.variant_id || '').trim();
    const variant = idToVariant.get(key) || null;
    const next = { ...r };
    if (!variant) {
      next._invalid = true;
    } else {
      next.product_title = variant.title || '';
      next.price = variant.price || '';
    }
    return next;
  });
};

/**
 * Orchestrate bulk order creation from parsed CSV rows.
 */
export const processOrderCreateCsv = async ({
  rows,
  databaseAPI: api = databaseAPI,
  fetchVariant,
  alert: alertFn = window.alert,
  onDone,
  onEnriched,
}) => {
  if (!rows || rows.length === 0) {
    alertFn('No CSV data to process');
    return { success: 0, failed: 0 };
  }

  const enrichFn = fetchVariant
    ? (r) => enrichCsvRowsWithVariantInfo(r, fetchVariant)
    : enrichCsvRowsWithVariantInfo;

  let success = 0;
  let failed = 0;
  const enriched = await enrichFn(rows);
  if (typeof onEnriched === 'function') onEnriched(enriched);

  const groupKeyOf = (r) => {
    const addr = r.shipping_address || {};
    const addrKey = `${addr.address1 || ''}|${addr.city || ''}|${addr.province || ''}|${addr.country || ''}|${addr.zip || ''}|${addr.phone_number || ''}`;
    const couponKey = (r.coupon_code || '').trim();
    return `${(r.customer_email || '').trim().toLowerCase()}|${(r.currency || 'INR').trim().toUpperCase()}|${addrKey}|${couponKey}`;
  };
  const groups = new Map();
  for (const row of enriched) {
    if (!row.variant_id || row._invalid) { failed++; continue; }
    const key = groupKeyOf(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }

  for (const [, groupRows] of groups) {
    try {
      if (!groupRows || groupRows.length === 0) { failed++; continue; }
      const base = groupRows[0];
      const variantQty = new Map();
      for (const r of groupRows) {
        const vid = String(r.variant_id).trim();
        const qty = Math.max(1, parseInt(r.quantity || 1, 10));
        variantQty.set(vid, (variantQty.get(vid) || 0) + qty);
      }
      const line_items = Array.from(variantQty.entries()).map(([vid, qty]) => ({ variant_id: vid, quantity: qty }));

      const payload = {
        customer_email: base.customer_email,
        currency: base.currency || 'INR',
        line_items,
        shipping_address: base.shipping_address,
        billing_address: base.shipping_address,
        ...(base.coupon_code ? { discount_codes: [{ code: base.coupon_code }] } : {})
      };
      if (base.customer_first_name) payload.customer_first_name = base.customer_first_name;
      if (base.customer_last_name) payload.customer_last_name = base.customer_last_name;

      const res = await api.createOrderInShopify(payload);
      if (res && res.success) success++; else failed++;
    } catch (_e) {
      failed++;
    }
  }

  const totalGroups = groups.size;
  alertFn(`CSV Create Complete:\n✅ Success: ${success}\n❌ Failed: ${failed}\n👥 Groups (orders): ${totalGroups}`);
  if (typeof onDone === 'function') onDone({ success, failed, totalGroups });
  return { success, failed, totalGroups };
};
